import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Calendar, Clock, AlertTriangle, Check, X, Settings, Loader2 } from 'lucide-react';
import { Switch } from '../ui/Switch';
import PageLoader from '../ui/PageLoader';
import toast from 'react-hot-toast';
import mfaService from '../../services/mfaService';
import { AuthContext } from '../../contexts/AuthContext';
import hasUserRole from '../../utils/UserUtil';

const StudyMFASettings = ({ studyId }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mfaSettings, setMfaSettings] = useState({
    mfa_required_for_all_users: false,
    mfa_enforcement_date: '',
    grace_period_days: 30,
    users: []
  });
  const [originalSettings, setOriginalSettings] = useState(null);

  // Check if user can manage MFA settings
  const canManageMFA = hasUserRole(user, ['Program Admin', 'Study Coordinator', 'Investigator']) || user?.is_superuser;

  useEffect(() => {
    loadMFASettings();
  }, [studyId]);

  const loadMFASettings = async () => {
    try {
      setLoading(true);
      const data = await mfaService.getStudyMFASettings(studyId);
      setMfaSettings(data);
      setOriginalSettings(data);
    } catch (error) {
      console.error('Error loading MFA settings:', error);
      if (error.response?.status === 404) {
        // No MFA settings exist yet, use defaults
        const defaultSettings = {
          mfa_required_for_all_users: false,
          mfa_enforcement_date: '',
          grace_period_days: 30,
          users: []
        };
        setMfaSettings(defaultSettings);
        setOriginalSettings(defaultSettings);
      } else {
        toast.error('Failed to load MFA settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setMfaSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const settingsToSave = {
        mfa_required_for_all_users: mfaSettings.mfa_required_for_all_users,
        mfa_enforcement_date: mfaSettings.mfa_enforcement_date || null,
        grace_period_days: mfaSettings.grace_period_days
      };
      
      const updatedSettings = await mfaService.updateStudyMFASettings(studyId, settingsToSave);
      setMfaSettings(updatedSettings);
      setOriginalSettings(updatedSettings);
      toast.success('MFA settings updated successfully');
    } catch (error) {
      console.error('Error saving MFA settings:', error);
      toast.error('Failed to save MFA settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUserMFAOverride = async (userId, override) => {
    try {
      await mfaService.overrideUserMFA(studyId, userId, { mfa_required: override });
      toast.success(`User MFA requirement ${override ? 'enabled' : 'disabled'}`);
      // Reload settings to get updated user list
      loadMFASettings();
    } catch (error) {
      console.error('Error overriding user MFA:', error);
      toast.error('Failed to update user MFA requirement');
    }
  };

  const hasUnsavedChanges = () => {
    if (!originalSettings) return false;
    return (
      mfaSettings.mfa_required_for_all_users !== originalSettings.mfa_required_for_all_users ||
      mfaSettings.mfa_enforcement_date !== originalSettings.mfa_enforcement_date ||
      mfaSettings.grace_period_days !== originalSettings.grace_period_days
    );
  };

  const getEnforcementStatus = () => {
    if (!mfaSettings.mfa_required_for_all_users) {
      return { status: 'disabled', text: 'MFA is not required for this study', color: 'text-gray-500 dark:text-gray-400' };
    }
    
    if (!mfaSettings.mfa_enforcement_date) {
      return { status: 'active', text: 'MFA is required immediately', color: 'text-success-600 dark:text-success-400' };
    }
    
    const enforcementDate = new Date(mfaSettings.mfa_enforcement_date);
    const now = new Date();
    
    if (enforcementDate > now) {
      const daysUntil = Math.ceil((enforcementDate - now) / (1000 * 60 * 60 * 24));
      return { 
        status: 'pending', 
        text: `MFA enforcement starts in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`, 
        color: 'text-warning-600 dark:text-warning-400' 
      };
    }
    
    return { status: 'active', text: 'MFA enforcement is active', color: 'text-success-600 dark:text-success-400' };
  };

  if (loading) {
    return <PageLoader className="py-12" />;
  }

  if (!canManageMFA) {
    return (
      <div className="rounded-xl border border-warning-500/30 bg-warning-50 p-6 dark:bg-warning-500/10">
        <div className="flex items-center">
          <AlertTriangle className="mr-2 h-5 w-5 flex-shrink-0 text-warning-600 dark:text-warning-400" />
          <p className="text-sm text-warning-800 dark:text-warning-400">
            You don't have permission to manage MFA settings for this study.
          </p>
        </div>
      </div>
    );
  }

  const enforcementStatus = getEnforcementStatus();
  // The counts use the same rules as the user table below (the status sits under mfa_status).
  const isMfaConfigured = (user) => !!(user.mfa_status?.mfa_setup_completed || user.mfa_status?.is_mfa_enabled);
  const isMfaRequired = (user) =>
    user.mfa_status?.mfa_required === true || mfaSettings.mfa_required_for_all_users === true;

  const panelClass = 'rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800';
  const panelTitleClass = 'flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="space-y-6"
    >
      {/* Status Overview */}
      <div className={panelClass}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className={panelTitleClass}>
            <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            MFA Status Overview
          </h3>
          <div className={`flex items-center text-sm ${enforcementStatus.color}`}>
            <div className={`mr-2 h-2 w-2 rounded-full ${
              enforcementStatus.status === 'active' ? 'bg-success-500' :
              enforcementStatus.status === 'pending' ? 'bg-warning-500' : 'bg-gray-400 dark:bg-gray-500'
            }`}></div>
            {enforcementStatus.text}
          </div>
        </div>
        
        {mfaSettings.users && mfaSettings.users.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-xl bg-gray-50 p-4 text-center dark:bg-gray-900/40">
              <div className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">
                {mfaSettings.users.length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Total Users</div>
            </div>
            <div className="rounded-xl bg-success-50 p-4 text-center dark:bg-success-500/10">
              <div className="text-2xl font-extrabold tabular-nums text-success-600 dark:text-success-400">
                {mfaSettings.users.filter(isMfaConfigured).length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">MFA Configured</div>
            </div>
            <div className="rounded-xl bg-danger-50 p-4 text-center dark:bg-danger-500/10">
              <div className="text-2xl font-extrabold tabular-nums text-danger-600 dark:text-danger-400">
                {mfaSettings.users.filter((u) => isMfaRequired(u) && !isMfaConfigured(u)).length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Pending Setup</div>
            </div>
          </div>
        )}
      </div>

      {/* MFA Settings */}
      <div className={panelClass}>
        <h3 className={`mb-4 ${panelTitleClass}`}>
          <Settings className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          MFA Configuration
        </h3>
        
        <div className="space-y-6">
          {/* Enable MFA Toggle */}
          <div className="flex items-center justify-between gap-4">
            <div>
              <label htmlFor="mfa_required_for_all_users" className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200">
                Require MFA for all study users
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                When enabled, all users associated with this study must set up and use MFA
              </p>
            </div>
            <Switch
              id="mfa_required_for_all_users"
              checked={mfaSettings.mfa_required_for_all_users}
              onChange={(e) => handleSettingsChange('mfa_required_for_all_users', e.target.checked)}
            />
          </div>

          {/* Enforcement Date */}
          {mfaSettings.mfa_required_for_all_users && (
            <div>
              <label className="label flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                Enforcement Date (Optional)
              </label>
              <input
                type="date"
                value={mfaSettings.mfa_enforcement_date}
                onChange={(e) => handleSettingsChange('mfa_enforcement_date', e.target.value)}
                className="input"
              />
              <p className="hint">
                Leave empty to require MFA immediately. If set, users will have until this date to set up MFA.
              </p>
            </div>
          )}

          {/* Grace Period */}
          {mfaSettings.mfa_required_for_all_users && (
            <div>
              <label className="label flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                Grace Period (Days)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={mfaSettings.grace_period_days}
                onChange={(e) => handleSettingsChange('grace_period_days', parseInt(e.target.value) || 30)}
                className="input"
              />
              <p className="hint">
                Number of days users have to set up MFA after the requirement becomes active.
              </p>
            </div>
          )}

          {/* Save Button */}
          {hasUnsavedChanges() && (
            <div className="flex justify-end border-t border-gray-100 pt-4 dark:border-gray-700">
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* User Management */}
      {mfaSettings.users && mfaSettings.users.length > 0 && (
        <div className={panelClass}>
          <h3 className={`mb-4 ${panelTitleClass}`}>
            <Users className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            User MFA Status
          </h3>
          
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="th-cell">
                      User
                    </th>
                    <th className="th-cell">
                      Role
                    </th>
                    <th className="th-cell">
                      MFA Status
                    </th>
                    <th className="th-cell">
                      Required
                    </th>
                    <th className="th-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {mfaSettings.users.map((user) => (
                    <tr key={user.id} className="tr-interactive">
                      <td className="td-cell whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                        </div>
                      </td>
                      <td className="td-cell whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {user.role_in_study}
                      </td>
                      <td className="td-cell whitespace-nowrap">
                        <span className={`badge ${
                          (user.mfa_status?.mfa_setup_completed || user.mfa_status?.is_mfa_enabled)
                            ? 'badge-success' 
                            : 'badge-danger'
                        }`}>
                          {user.mfa_status?.mfa_setup_completed || user.mfa_status?.is_mfa_enabled ? (
                            <>
                              <Check className="h-3 w-3" />
                              Configured
                            </>
                          ) : (
                            <>
                              <X className="h-3 w-3" />
                              Not Set Up
                            </>
                          )}
                        </span>
                      </td>
                      <td className="td-cell whitespace-nowrap">
                        <span className={`badge ${
                          (user.mfa_status?.mfa_required === true || mfaSettings.mfa_required_for_all_users === true)
                            ? 'badge-danger' 
                            : 'badge-gray'
                        }`}>
                          {(user.mfa_status?.mfa_required === true || mfaSettings.mfa_required_for_all_users === true) ? 'Required' : 'Optional'}
                        </span>
                      </td>
                      <td className="td-cell whitespace-nowrap">
                        {/* Hide "Make Optional" button when MFA is required (either by study policy or individual requirement) */}
                        {!(user.mfa_status?.mfa_required === true || mfaSettings.mfa_required_for_all_users === true) && (
                          <button
                            onClick={() => handleUserMFAOverride(user.user_id, !user.mfa_status?.mfa_required)}
                            className="inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-500/10"
                          >
                            Require MFA
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StudyMFASettings;
